import React, { useMemo } from "react";
import styled from "styled-components";
import { colors2 } from "oolib";
import {
  UI_BODY_SEMIBOLD_SM_DF,
  UI_CAPTION,
} from "oolib/dist/v2/components/Typo2";
import { SquaresFour } from "@phosphor-icons/react";
import { DashboardWidgetShell } from "@/_self_managed/SM_Dashboard/comps/DashboardWidgetShell";
import { ZoneCard } from "./ZoneCard";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { resolveZones } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/zoneStyles";
import { toneColor, momentumBasisText } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";

/**
 * The six zones as the field's own grid — severity band by row, momentum by
 * column — each cell with how many sub-topics the scorer put there and
 * what share of the period's observations they carry (the bar). Same
 * scored items as the field; the zone copy (label, hint, colour) comes from
 * the tenant's config.
 */
const GRID = [
  ["resolving", "chronic", "fires"],
  ["fading", "background", "emerging"],
];
const COLUMNS = [
  { label: "← Cooling", tone: "down" },
  { label: "Steady", tone: "flat" },
  { label: "Rising →", tone: "up" },
];
const ROWS = ["High", "Low"];

const Layout = styled.div`
  padding-top: 12px;
  display: grid;
  grid-template-columns: 64px repeat(3, minmax(0, 1fr));
  grid-template-rows: auto repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
  align-items: stretch;
`;
const ColHead = styled.div`
  text-align: center;
  /* Sits closer to the cells it heads than the row gap would put it. */
  margin-bottom: -4px;
`;
const RowHead = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  text-align: right;
`;
const Foot = styled.div`
  padding-top: 6px;
`;
// A share that rounds to nothing still reads as something.
const shareText = (share) =>
  share > 0 && share < 0.5 ? "<1%" : `${Math.round(share)}%`;

export const ZonesGridWidget = ({ items, meta, blurNote, blurNoteIcon }) => {
  const categoryTheme = useSignalCategories();
  const zones = resolveZones(categoryTheme.prioritiesCopy);
  const stats = useMemo(() => {
    const total = items.reduce((acc, item) => acc + (item.n || 0), 0);
    const out = {};
    for (const item of items) {
      if (!item.zone) continue;
      out[item.zone] ||= { count: 0, n: 0 };
      out[item.zone].count += 1;
      out[item.zone].n += item.n || 0;
    }
    for (const zone of Object.keys(out))
      out[zone].share = total ? (out[zone].n / total) * 100 : 0;
    return out;
  }, [items]);

  return (
    <DashboardWidgetShell
      titleJSX={
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <SquaresFour size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF>Zones</UI_BODY_SEMIBOLD_SM_DF>
        </div>
      }
      style={{ gap: 0 }}
          blurNote={blurNote}
      blurNoteIcon={blurNoteIcon}
    >
      <Layout>
        <div />
        {COLUMNS.map((col) => (
          <ColHead key={col.label}>
            <UI_CAPTION
              style={{
                color:
                  col.tone === "flat" ? colors2.grey60 : toneColor(col.tone),
              }}
            >
              {col.label}
            </UI_CAPTION>
          </ColHead>
        ))}
        {GRID.map((row, r) => (
          <React.Fragment key={ROWS[r]}>
            <RowHead>
              <UI_CAPTION style={{ color: colors2.grey60 }}>
                {ROWS[r]}
              </UI_CAPTION>
              <UI_CAPTION style={{ color: colors2.grey60 }}>
                severity
              </UI_CAPTION>
            </RowHead>
            {row.map((zone) => {
              const z = zones[zone];
              const s = stats[zone] || { count: 0, share: 0 };
              return (
                <ZoneCard
                  key={zone}
                  zone={z}
                  count={s.count}
                  caption={`${shareText(s.share)} of obs`}
                  share={s.share}
                  tint
                />
              );
            })}
          </React.Fragment>
        ))}
      </Layout>
      <Foot>
        <UI_CAPTION style={{ color: colors2.grey40 }}>
          {`Count of sub-topics; bar is share of observations. ${meta.nowLabel}${momentumBasisText(meta) ? `, ${momentumBasisText(meta)}` : ""}.`}
        </UI_CAPTION>
      </Foot>
    </DashboardWidgetShell>
  );
};
